import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  Button, TextField, MenuItem, Stack, Autocomplete, Chip, Paper, Divider, Typography,
  IconButton, Popper, Grid, Box, Tabs, Tab, CircularProgress, Snackbar, Alert
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { ReactSortable } from "react-sortablejs";
import supabase from "../../../../../utils/supabase";
import DialogJournalUpsert from "../../Dialogs/DialogJournalUpsert";

import AuditLogUpdater from "../../../AuditLogUpdater/AuditLogUpdater";
import { useMasterData } from "../../../../../utils/MasterDataProvider";
import { v4 as uuidv4 } from "uuid";
import ResearcherSelector from "../../Dialogs/parts/ResearcherSelector";
import BaseDialog from "../../Dialogs/Framework/BaseDialog";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

/**
 * props:
 * - open: boolean
 * - mode: "add" | "edit"
 * - publication?: object (edit のみ)
 * - onClose?: (saved?: boolean) => void
 * - onSaved?: (row) => void
 */
const buildDefaultForm = () => ({
  publication_type: "journal_article",
  id: `pub_${uuidv4()}`,
  id_uuid: uuidv4(),
  title_original: "",
  title_english: "",
  title_other: "",
  publication_date: "",
  online_first_date: "",
  journal_id: "",
  volume: "",
  number: "",
  article_id: "",
  page: "",
  doi: "",
  abstract_english: "",
  abstract_other: "",
  is_open_access: false,
  total_pages: "",
  city: "",
  country: "",
  parent_book_id: "",
  degree_name: "",
  remark: "",
  isbn_hardback: "",
  isbn_epub: "",
  isbn_pdf: "",
});

export default function DialogPublicationUpsert({ open, mode, publication, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const { journals: journalsMaster, countries: countriesMaster } = useMasterData();
  const masterLoadedRef = useRef(false);
  const masterCacheRef = useRef({ statuses: [], cities: [], parentBooks: [] });
  const [tabIndex, setTabIndex] = useState(0);

  // ===== State =====
  const [form, setForm] = useState(buildDefaultForm());

  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const [journals, setJournals] = useState([]);
  const [statuses, setStatuses] = useState([]); // 使っていないが既存のまま保持
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [parentBooks, setParentBooks] = useState([]);
  const [showAddJournal, setShowAddJournal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [auditLogProps, setAuditLogProps] = useState(null);
  const [doiLoading, setDoiLoading] = useState(false);
  const [doiSuccessSnackbarOpen, setDoiSuccessSnackbarOpen] = useState(false);

  // dirty 判定用
  const initialRef = useRef(null);
  const [authorsLoaded, setAuthorsLoaded] = useState(!isEdit);
  const [bootstrapped, setBootstrapped] = useState(false);

  const resetState = useCallback(() => {
    setForm(buildDefaultForm());
    setSelectedAuthors([]);
    setJournals([]);
    setStatuses([]);
    setCities([]);
    setCountries([]);
    setParentBooks([]);
    setShowAddJournal(false);
    setLoading(false);
    setAuditLogProps(null);
    setDoiLoading(false);
    setDoiSuccessSnackbarOpen(false);
    initialRef.current = null;
    setAuthorsLoaded(!isEdit);
    setBootstrapped(false);
    setTabIndex(0);
  }, [isEdit]);

  // ===== Helpers =====
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const sanitizePayload = (data) => {
    const cleaned = { ...data };
    if (!cleaned.id_uuid) cleaned.id_uuid = uuidv4();
    // 空文字は null に
    Object.keys(cleaned).forEach((key) => {
      if (cleaned[key] === "") cleaned[key] = null;
    });
    // 型合わせ
    cleaned.is_open_access = !!cleaned.is_open_access;

    if (!cleaned.total_pages?.toString().trim()) {
      cleaned.total_pages = null;
    } else {
      const n = Number(cleaned.total_pages);
      cleaned.total_pages = Number.isFinite(n) ? n : null;
    }

    if (!cleaned.journal_id) cleaned.journal_id = null;
    if (!cleaned.parent_book_id) cleaned.parent_book_id = null;
    if (!cleaned.city) cleaned.city = null;
    if (!cleaned.country?.toString().trim()) cleaned.country = null;
    return cleaned;
  };

  const requiredOk = useCallback(() => {
    // ID はUIから削除されたので、requiredチェックから除外
    const req = ["title_english", "title_original", "publication_date"];
    for (const k of req) {
      if (!String(form[k] || "").trim()) return false;
    }
    return true;
  }, [form]);

  // ===== Initial & lookups =====
  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }

    // 初期フォーム
    if (isEdit && publication) {
      const { _journal_name, _authors, _authors_full, _year, publications_authors, journal, ...rest } = publication;
      setForm({
        publication_type: rest.publication_type || "journal_article",
        id: rest.id || "",
        id_uuid: rest.id_uuid || rest.id_uuid?.toString?.() || uuidv4(),
        title_original: rest.title_original || "",
        title_english: rest.title_english || "",
        title_other: rest.title_other || "",
        publication_date: rest.publication_date?.slice(0, 10) || "",
        online_first_date: rest.online_first_date?.slice(0, 10) || "",
        journal_id: rest.journal_id || "",
        volume: rest.volume || "",
        number: rest.number || "",
        article_id: rest.article_id || "",
        page: rest.page || "",
        doi: rest.doi || "",
        abstract_english: rest.abstract_english || "",
        abstract_other: rest.abstract_other || "",
        is_open_access: !!rest.is_open_access,
        total_pages: rest.total_pages || "",
        city: rest.city || "",
        country: rest.country || "",
        parent_book_id: rest.parent_book_id || "",
        degree_name: rest.degree_name || "",
        remark: rest.remark || "",
        isbn_hardback: rest.isbn_hardback || "",
        isbn_epub: rest.isbn_epub || "",
        isbn_pdf: "",
      });
    } else {
      setForm(buildDefaultForm());
    }

    const setMastersFromCache = () => {
      setJournals(
        (journalsMaster || []).sort((a, b) =>
          (a.name_english || a.name_original || "").localeCompare(b.name_english || b.name_original || "")
        )
      );
      setStatuses(masterCacheRef.current.statuses || []);
      setCities(masterCacheRef.current.cities || []);
      setCountries(((countriesMaster || []).map((c) => c.id)).sort());
      setParentBooks(masterCacheRef.current.parentBooks || []);
    };

    if (!masterLoadedRef.current) {
      (async () => {
        console.log("[DialogPublicationUpsert] fetching master tables...");
        const [{ data: statusData }, { data: cityData }, { data: parentData }] = await Promise.all([
          supabase.from("registration_status").select("*"),
          supabase.from("cities").select("id, name"),
          supabase.from("publications").select("id, title_english").eq("publication_type", "book"),
        ]);

        masterCacheRef.current = {
          statuses: (statusData || []).sort((a, b) => (a.label || "").localeCompare(b.label || "")),
          cities: (cityData || []).sort((a, b) => (a.name || "").localeCompare(b.name || "")),
          parentBooks: parentData || [],
        };
        masterLoadedRef.current = true;
        setMastersFromCache();
        console.log("[DialogPublicationUpsert] masters fetched", {
          statuses: masterCacheRef.current.statuses.length,
          cities: masterCacheRef.current.cities.length,
          parentBooks: masterCacheRef.current.parentBooks.length,
        });
      })();
    } else {
      setMastersFromCache();
    }

    // 著者（編集時）
    (async () => {
      if (!isEdit || !publication?.id) {
        setSelectedAuthors([]);
        setAuthorsLoaded(true);
        return;
      }
      const { data: authRel } = await supabase
        .from("publications_authors")
        .select("author_order, researcher:researcher_id ( id, first_name, last_name )")
        .eq("publication_id", publication.id)
        .order("author_order");
      const authors = (authRel || []).map((r) => r?.researcher).filter(Boolean);
      setSelectedAuthors(authors);
      setAuthorsLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, publication?.id, resetState]);

  // マスター更新を反映（初回の load 後にも同期）
  useEffect(() => {
    if (!open) return;
    const sorted = (journalsMaster || []).sort((a, b) =>
      (a.name_english || a.name_original || "").localeCompare(b.name_english || b.name_original || "")
    );
    console.log("[DialogPublicationUpsert] journalsMaster update", sorted.length, sorted.slice(0, 3));
    setJournals(sorted);
    setCountries(((countriesMaster || []).map((c) => c.id)).sort());
  }, [open, journalsMaster, countriesMaster]);

  // 初期 dirty スナップショットは著者ロード完了後に一度だけ
  useEffect(() => {
    if (!open || bootstrapped || !authorsLoaded) return;
    initialRef.current = JSON.stringify({ form, selectedAuthors });
    setBootstrapped(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, authorsLoaded, bootstrapped]);

  const isDirty = useMemo(() => {
    const snap = initialRef.current;
    if (!snap || !bootstrapped) return false;
    return JSON.stringify({ form, selectedAuthors }) !== snap;
  }, [form, selectedAuthors, bootstrapped]);

  // ===== DOI (Crossref) =====
  const formatDateParts = (dateParts) => {
    if (!Array.isArray(dateParts)) return null;
    const [year, month = 1, day = 1] = dateParts;
    const m = String(month).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    return `${year}-${m}-${d}`;
  };
  const extractDOI = (input) => {
    if (!input) return null;
    return String(input).replace(/^https?:\/\/(dx\.)?doi\.org\//, "").trim();
  };
  const normalizeStr = (str) => {
    if (typeof str !== "string") return "";
    return str.replace(/_/g, "").replace(/\s/g, "").toLowerCase();
  };

  const fetchDOIData = async (rawDoiInput) => {
    const doi = extractDOI(rawDoiInput);
    if (!doi) {
      alert("No DOI inputted!");
      return;
    }
    setDoiLoading(true);
    try {
      const response = await fetch(`https://api.crossref.org/works/${encodeURIComponent(doi)}`);
      const json = await response.json();
      const work = json.message;
      if (!work) {
        alert("No data available from this DOI.");
        return;
      }

      const firstParts = (obj) => (Array.isArray(obj?.["date-parts"]) ? obj["date-parts"][0] : null);
      const issuedParts =
        firstParts(work.issued) ||
        firstParts(work["published-print"]) ||
        firstParts(work["published-online"]);
      const onlineParts = firstParts(work["published-online"]);
      const issuedDate = issuedParts ? formatDateParts(issuedParts) : null;
      const onlineDate = onlineParts ? formatDateParts(onlineParts) : null;

      setForm((prev) => {
        const containerTitle = work["container-title"]?.[0] || "";
        const matchedJournal = journals.find(
          (journal) => normalizeStr(journal.name_english) === normalizeStr(containerTitle)
        );
        return {
          ...prev,
          title_english: work.title?.[0] || prev.title_english,
          publication_date: issuedDate || prev.publication_date,
          online_first_date: onlineDate || prev.online_first_date,
          abstract_english: work.abstract ? work.abstract.replace(/<[^>]+>/g, "") : prev.abstract_english,
          journal_id: matchedJournal?.id || prev.journal_id || null,
          volume: work.volume || prev.volume || null,
          number: work.issue || prev.number || null,
          page: work.page || prev.page || null,
          doi: doi,
        };
      });
      setDoiSuccessSnackbarOpen(true);
    } catch {
      alert("Failed to get article data using this doi...");
    } finally {
      setDoiLoading(false);
    }
  };

  // ===== Authors =====
  const handleAuthorRemove = (id) => {
    setSelectedAuthors((prev) => prev.filter((a) => a.id !== id));
  };

  const selectedAuthorChips = useMemo(
    () => (
      <ReactSortable list={selectedAuthors} setList={setSelectedAuthors}>
        {selectedAuthors.map((a, i) => (
          <Chip
            key={a.id}
            label={`${i + 1}. ${a.last_name}, ${a.first_name}`}
            onDelete={() => handleAuthorRemove(a.id)}
            sx={{ m: 0.5, cursor: "grab" }}
            deleteIcon={<CloseIcon />}
          />
        ))}
      </ReactSortable>
    ),
    [selectedAuthors]
  );

  // ===== Journal add inline =====
  const handleJournalAdd = (newJournal) => {
    const next = [...journals, newJournal].sort((a, b) =>
      (a.name_english || "").localeCompare(b.name_english || "")
    );
    setJournals(next);
    setForm((p) => ({ ...p, journal_id: newJournal.id }));
  };

  const journalInputRef = useRef(null);
  const CustomPopper = (props) => (
    <Popper {...props} placement="bottom-start">
      <Paper sx={{ p: 1 }}>
        <Stack direction="row" justifyContent="flex-end">
          <IconButton size="small" onClick={() => journalInputRef.current?.blur()}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        {props.children}
      </Paper>
    </Popper>
  );

  // ===== Submit / Delete =====
  const handleSave = async () => {
    if (!requiredOk()) {
      alert("Required fields are missing.");
      return;
    }
    setLoading(true);
    try {
      const payload = sanitizePayload(form);
      // DOI 重複チェック（NULL/空はスキップ）
      if (payload.doi) {
        const { data: doiDup, error: doiErr } = await supabase
          .from("publications")
          .select("id")
          .eq("doi", payload.doi)
          .neq("id", form.id);
        if (doiErr) throw doiErr;
        if (doiDup?.length) {
          alert(`Duplicate DOI found in publication ID: ${doiDup[0].id}. Please use a unique DOI or clear the value.`);
          return;
        }
      }

      if (!isEdit) {
        // duplicate check
        const { data: dup } = await supabase.from("publications").select("id").eq("id", form.id);
        if (dup?.length > 0) {
          alert("Duplicate ID exists.");
          return;
        }
        const { error: insErr } = await supabase.from("publications").insert([payload]);
        if (insErr) throw insErr;

        if (selectedAuthors.length > 0) {
          const relations = selectedAuthors.map((a, i) => ({
            publication_id: form.id,
            publication_id_uuid: form.id_uuid,
            researcher_id: a.id,
            author_order: i + 1,
          }));
          const { error: relErr } = await supabase.from("publications_authors").insert(relations);
          if (relErr) throw relErr;
        }

        // Audit Log (INSERT)
        setAuditLogProps({
          tableName: "publications",
          rowId: form.id,
          action: "INSERT",
          beforeData: null,
          afterData: payload,
          onComplete: () => {
            alert("Publication successfully registered and audit logged!");
            onSaved?.(payload); // 必要なら select で取り直し
            onClose?.(true);
          },
        });
      } else {
        // UPDATE
        const { error: updateError } = await supabase
          .from("publications")
          .update(payload)
          .eq("id", form.id);
        if (updateError) throw updateError;

        // authors re-link
        await supabase.from("publications_authors").delete().eq("publication_id", form.id);
        if (selectedAuthors.length > 0) {
          const relations = selectedAuthors.map((a, i) => ({
            publication_id: form.id,
            publication_id_uuid: form.id_uuid,
            researcher_id: a.id,
            author_order: i + 1,
          }));
          const { error: relErr } = await supabase.from("publications_authors").insert(relations);
          if (relErr) throw relErr;
        }

        // Audit Log (UPDATE)
        setAuditLogProps({
          tableName: "publications",
          rowId: form.id,
          action: "UPDATE",
          beforeData: publication ?? null,
          afterData: payload,
          onComplete: () => {
            alert("Update successful.");
            onSaved?.(payload);
            onClose?.(true);
          },
        });
      }
    } catch (e) {
      console.error(e);
      alert(`Save failed: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      // 関連テーブルを先に削除
      await supabase.from("publications_authors").delete().eq("publication_id", form.id);
      // 本体削除
      const { error } = await supabase.from("publications").delete().eq("id", form.id);
      if (error) throw error;

      // Audit Log (DELETE) — beforeData は現在の form（sanitize 済み）を使用
      setAuditLogProps({
        tableName: "publications",
        rowId: form.id,
        action: "DELETE",
        beforeData: sanitizePayload(form),
        afterData: null,
        onComplete: () => {
          alert("Deleted successfully");
          onClose?.(true);
        },
      });
    } catch (e) {
      console.error(e);
      alert(`Delete failed: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // ===== Render =====
  return (
    <>
      <BaseDialog
        open={open}
        title={isEdit ? "Edit Publication" : "Add Publication"}
        onClose={() => onClose?.(false)}
        onSubmit={handleSave}
        submitText={isEdit ? "Save" : "Register"}
        onDelete={isEdit ? handleDelete : undefined}
        deleteText="Delete"
        loading={loading}
        confirmCloseWhen={!loading && isDirty}
        confirmCloseMessage="You have unsaved changes. Close without saving?"
        maxWidth="lg"
        fullWidth
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabIndex} onChange={handleTabChange} aria-label="publication tabs">
            <Tab label="Basic Info" />
            <Tab label="Authors" />
            <Tab label="Date" />
            <Tab label="Title" />
            <Tab label="Details" />
            <Tab label="Abstract" />
            <Tab label="Other" />
          </Tabs>
        </Box>

        <TabPanel value={tabIndex} index={0}>
          {/* Publication Type */}
          {!isEdit && (
            <TextField
              select
              label="Publication Type"
              value={form.publication_type}
              onChange={(e) => setField("publication_type", e.target.value)}
              fullWidth
              margin="dense"
            >
              <MenuItem value="journal_article">Journal Article</MenuItem>
              <MenuItem value="book">Book</MenuItem>
              <MenuItem value="book_chapter">Book Chapter</MenuItem>
              <MenuItem value="thesis">Thesis</MenuItem>
            </TextField>
          )}

          {/* DOI Helper */}
          <Divider sx={{ my: 3, borderWidth: 1.5, borderColor: "primary.light" }} />
          <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
            Enter Data By DOI
          </Typography>
          <Typography variant="caption">
            If a DOI is available, part of the input form can be automatically filled in using the DOI.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ my: 1 }}>
            <TextField
              label="DOI (If available)"
              value={form.doi || ""}
              onChange={(e) => setField("doi", e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  fetchDOIData(form.doi);
                }
              }}
              fullWidth
              margin="dense"
              disabled={doiLoading}
              InputProps={{
                endAdornment: doiLoading ? <CircularProgress size={20} /> : null,
              }}
            />
            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={() => fetchDOIData(form.doi)} disabled={doiLoading}>
              GET
            </Button>
          </Stack>
        </TabPanel>

        <TabPanel value={tabIndex} index={1}>
          {/* Authors */}
          <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
            Authors
          </Typography>
          <ResearcherSelector
            value={null}
            onChange={async (selectedId) => {
              if (selectedId && !selectedAuthors.find((x) => x.id === selectedId)) {
                const { data } = await supabase.from("researchers").select("*").eq("id", selectedId).single();
                if (data) setSelectedAuthors((prev) => [...prev, data]);
              }
            }}
            label="Search and Add Authors"
          />
          <Box border={1} borderColor="divider" borderRadius={1} p={1} mt={1} minHeight={56}>
            <Typography variant="caption">Selected Authors (Drag to reorder)</Typography>
            {selectedAuthorChips}
          </Box>
        </TabPanel>

        <TabPanel value={tabIndex} index={2}>
          {/* Dates */}
          <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
            Publication Date
          </Typography>
          <TextField
            label="Publication Date"
            type="date"
            value={form.publication_date || ""}
            onChange={(e) => setField("publication_date", e.target.value)}
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
            error={!form.publication_date?.trim()}
            required
          />
          <TextField
            label="Online First Date"
            type="date"
            value={form.online_first_date || ""}
            onChange={(e) => setField("online_first_date", e.target.value)}
            fullWidth
            margin="dense"
            InputLabelProps={{ shrink: true }}
          />
        </TabPanel>

        <TabPanel value={tabIndex} index={3}>
          {/* Titles */}
          <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
            Title
          </Typography>
          <TextField
            label="Title (Original) (Require)"
            value={form.title_original || ""}
            onChange={(e) => setField("title_original", e.target.value)}
            fullWidth
            margin="dense"
            multiline
            error={!form.title_original?.trim()}
            required
          />
          <TextField
            label="Title (English) (Require)"
            value={form.title_english || ""}
            onChange={(e) => setField("title_english", e.target.value)}
            fullWidth
            margin="dense"
            multiline
            error={!form.title_english?.trim()}
            required
          />
          <TextField
            label="Title (Other)"
            value={form.title_other || ""}
            onChange={(e) => setField("title_other", e.target.value)}
            fullWidth
            margin="dense"
          />
        </TabPanel>

        <TabPanel value={tabIndex} index={4}>
          {/* Journal & Article Info */}
          {form.publication_type === "journal_article" && (
            <>
              <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
                Journal and Article Info
              </Typography>
              <Autocomplete
                options={journals}
                getOptionLabel={(j) =>
                  j.name_english === j.name_original ? j.name_english : `${j.name_english} (${j.name_original})`
                }
                value={journals.find((j) => String(j.id) === String(form.journal_id)) || null}
                onChange={(e, val) => setField("journal_id", val?.id || null)}
                renderInput={(params) => (
                  <TextField {...params} label="Journal" margin="dense" inputRef={journalInputRef} />
                )}
                PopperComponent={CustomPopper}
              />
              <Stack direction="row" justifyContent="flex-end" mt={1}>
                <Button size="small" startIcon={<AddIcon />} onClick={() => setShowAddJournal(true)}>
                  + Add/Edit Journal
                </Button>
              </Stack>
              <Grid container spacing={1}>
                {["volume", "number", "article_id", "page"].map((field) => (
                  <Grid item xs={12} sm={6} md={3} key={field}>
                    <TextField
                      label={field.replace("_", " ").toUpperCase()}
                      value={form[field] || ""}
                      onChange={(e) => setField(field, e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {/* Book */}
          {form.publication_type === "book" && (
            <>
              <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
                Book Info
              </Typography>
              <TextField
                label="Total Pages"
                value={form.total_pages || ""}
                onChange={(e) => setField("total_pages", e.target.value)}
                fullWidth
                margin="dense"
              />
              <Autocomplete
                options={cities}
                getOptionLabel={(c) => c.name}
                value={cities.find((c) => c.id === form.city) || null}
                onChange={(e, val) => setField("city", val?.id || null)}
                renderInput={(params) => <TextField {...params} label="City" margin="dense" />}
              />
              <Autocomplete
                options={countries}
                value={form.country || null}
                onChange={(e, val) => setField("country", val)}
                renderInput={(params) => <TextField {...params} label="Country" margin="dense" />}
              />
              <Grid container spacing={2}>
                {["isbn_hardback", "isbn_epub", "isbn_pdf"].map((field) => (
                  <Grid item xs={12} sm={4} key={field}>
                    <TextField
                      label={field.replace("isbn_", "").toUpperCase() + " ISBN"}
                      value={form[field] || ""}
                      onChange={(e) => setField(field, e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {/* Book Chapter */}
          {form.publication_type === "book_chapter" && (
            <>
              <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
                Book Chapter Info
              </Typography>
              <Autocomplete
                options={parentBooks}
                getOptionLabel={(b) => `${b.title_english} (${b.id})`}
                value={parentBooks.find((b) => b.id === form.parent_book_id) || null}
                onChange={(e, val) => setField("parent_book_id", val?.id || null)}
                renderInput={(params) => <TextField {...params} label="Parent Book" margin="dense" />}
              />
            </>
          )}

          {/* Thesis */}
          {form.publication_type === "thesis" && (
            <>
              <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
                Thesis Info
              </Typography>
              <TextField
                label="Degree Name"
                value={form.degree_name || ""}
                onChange={(e) => setField("degree_name", e.target.value)}
                fullWidth
                margin="dense"
              />
            </>
          )}
        </TabPanel>

        <TabPanel value={tabIndex} index={5}>
          {/* Abstract */}
          <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
            Abstract
          </Typography>
          {["abstract_english", "abstract_other"].map((field) => (
            <TextField
              key={field}
              label={field.replace("_", " ").toUpperCase()}
              value={form[field] || ""}
              onChange={(e) => setField(field, e.target.value)}
              fullWidth
              margin="dense"
              multiline
            />
          ))}
        </TabPanel>

        <TabPanel value={tabIndex} index={6}>
          {/* Other */}
          <Typography variant="h6" gutterBottom sx={{ color: "teal" }}>
            Other
          </Typography>
          <TextField
            select
            label="Open Access"
            value={form.is_open_access ? "true" : "false"}
            onChange={(e) => setField("is_open_access", e.target.value === "true")}
            fullWidth
            margin="dense"
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </TextField>

          <TextField
            label="Remark"
            value={form.remark || ""}
            onChange={(e) => setField("remark", e.target.value)}
            fullWidth
            margin="dense"
            multiline
          />
        </TabPanel>

      </BaseDialog>

      {/* Inline Journal Upsert */}
      <DialogJournalUpsert
        open={showAddJournal}
        mode="add"
        onClose={() => setShowAddJournal(false)}
        onSaved={handleJournalAdd}
      />

      {/* Audit log */}
      {auditLogProps && <AuditLogUpdater {...auditLogProps} />}

      <Snackbar open={doiSuccessSnackbarOpen} autoHideDuration={3000} onClose={() => setDoiSuccessSnackbarOpen(false)}>
        <Alert onClose={() => setDoiSuccessSnackbarOpen(false)} severity="success" sx={{ width: '100%' }}>
          DOI data fetched successfully!
        </Alert>
      </Snackbar>
    </>
  );
}